import { formatDate } from "./dateUtils.mjs"

const SUPPORTED_IMAGE_FORMATS_FOR_PDF_CONVERSION = ["jpeg", "jpg", "png", "jfif"]
const SUPPORTED_SOURCE_FORMATS_FOR_PDF_CONVERSION = [
  // "csv", // => Seems to be unsupported!
  "doc",
  "docx",
  "odp",
  "ods",
  "odt",
  "pot",
  "potm",
  "potx",
  "pps",
  "ppsx",
  "ppsxm",
  "ppt",
  "pptm",
  "pptx",
  "rtf",
  "xls",
  "xlsx",
]

const imageAllowedExtensions = ["jpeg", "jpg", "png", "webp", "gif", "svg", "heic"]

/**
 * Pauses execution for a specified number of milliseconds.
 *
 * @param {number} ms - The number of milliseconds to sleep.
 * @returns {Promise<void>} A promise that resolves after the specified delay.
 *
 * @example
 * sleep(1000).then(() => {
 *   console.log("Slept for 1 second");
 * });
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Converts a plural noun to its singular form based on simple English rules.
 *
 * Handles common plural endings like "ies", "ches", "xes", and trailing "s".
 * Special case for "movies" to "movie" as an exception.
 * Preserves the original casing (uppercase stays uppercase).
 *
 * @param {string} name - The plural noun to convert.
 * @returns {string|undefined} The singular form of the noun, or undefined if input is falsy.
 *
 * @example
 * toSingular("bunnies"); // "bunny"
 * toSingular("movies");  // "movie"
 * toSingular("matches"); // "match"
 * toSingular("boxes");   // "box"
 * toSingular("cars");    // "car"
 *
 * @example
 * toSingular("CARS");    // "CAR" (preserves uppercase)
 */
function toSingular(name) {
  if (!name) return
  let result = name
  if (name.toLowerCase().endsWith("ies")) {
    // movies is used in the getting-started tutorial. Other exception might have to be added later
    if (name.toLowerCase() === "movies") result = name.replace(/ies/i, "ie")
    else result = name.replace(/ies$/i, "y")
  } else if (name.toLowerCase().endsWith("ches")) result = name.replace(/ches$/i, "ch")
  else if (name.toLowerCase().endsWith("xes")) result = name.replace(/xes$/i, "x")
  else if (name.toLowerCase().endsWith("s")) result = name.substring(0, name.length - 1) // Remove last "s"
  if (name.toUpperCase() === name) result = result.toUpperCase()
  return result
}

/**
 * Converts a singular noun to its plural form based on common English rules.
 *
 * Handles endings like "ch", "x", "y", and adds "s" when appropriate.
 * Leaves words ending with "s" unchanged.
 * Preserves the original casing (uppercase stays uppercase).
 *
 * @param {string} name - The singular noun to convert.
 * @returns {string|undefined} The plural form of the noun, or undefined if input is falsy.
 *
 * @example
 * toPlural("match");  // "matches"
 * toPlural("box");    // "boxes"
 * toPlural("puppy");  // "puppies"
 * toPlural("car");    // "cars"
 * toPlural("bus");    // "bus" (already ends with "s")
 *
 * @example
 * toPlural("BOX");    // "BOXES" (preserves uppercase)
 */
function toPlural(name) {
  if (!name) return
  let result
  if (name.toLowerCase().endsWith("ch")) result = name.replace(/ch$/i, "ches")
  else if (name.toLowerCase().endsWith("x")) result = name.replace(/x$/i, "xes")
  else if (name.toLowerCase().endsWith("y")) result = name.replace(/y$/i, "ies")
  else if (name.toLowerCase().endsWith("s")) result = name
  else result = name + "s"
  if (name.toUpperCase() === name) result = result.toUpperCase()
  return result
}

/**
 * Converts a camelCase or PascalCase string to a dashed-lowercase string.
 *
 * Inserts dashes before uppercase letters and converts all characters to lowercase.
 *
 * @param {string} name - The input string in camelCase or PascalCase format.
 * @returns {string|undefined} The dashed-lowercase string, or undefined if input is falsy.
 *
 * @example
 * toDashed("camelCase");     // "camel-case"
 * toDashed("PascalCaseTest"); // "pascal-case-test"
 * toDashed("simpletest");    // "simpletest" (no uppercase letters)
 */
function toDashed(name) {
  if (!name) return
  const lower = name.toLowerCase()
  let dashed = lower[0]
  for (let i = 1; i < lower.length; i++) {
    if (name[i] === lower[i]) dashed += lower[i]
    else dashed += "-" + lower[i]
  }
  return dashed
}

/**
 * Computes a full name by combining first and last names with a space.
 *
 * Trims whitespace from both names and handles cases where one or both names may be missing.
 *
 * @param {string} first - The first name.
 * @param {string} last - The last name.
 * @returns {string|undefined} The combined full name, a single name if one is missing, or undefined if both are missing.
 *
 * @example
 * computePersonName("John", "Doe");    // "John Doe"
 * computePersonName("John", "");       // "John"
 * computePersonName("", "Doe");        // "Doe"
 * computePersonName("", "");           // undefined
 */
function computePersonName(first, last) {
  if (!first) return last?.trim()
  if (!last) return first?.trim()
  return first?.trim() + " " + last?.trim()
}

/**
 * Splits an array into chunks of a specified size.
 *
 * For arrays of strings, concatenates elements into strings separated by `stringSeparator`.
 * For other types, creates subarrays of the specified size.
 *
 * @param {Array} array - The array to chunk.
 * @param {number} [nb=100] - The maximum size of each chunk.
 * @param {Object} [options={}] - Additional options.
 * @param {string} [options.stringSeparator=","] - Separator used to join string elements in each chunk.
 * @returns {Array} An array of chunks, either arrays or concatenated strings depending on input type.
 *
 * @example
 * chunk([1, 2, 3, 4, 5], 2);
 * // [[1, 2], [3, 4], [5]]
 *
 * @example
 * chunk(["a", "b", "c", "d"], 2);
 * // ["a,b", "c,d"]
 *
 * @example
 * chunk(["a", "b", "c", "d"], 2, { stringSeparator: "-" });
 * // ["a-b", "c-d"]
 */
function chunk(array, nb = 100, options = {}) {
  const { stringSeparator = "," } = options || {}

  const chunks = []
  let index = 0
  if (typeof array[0] === "string" && stringSeparator) {
    // array of string => concat
    for (let i = 0; i < array.length; i++) {
      if (!chunks[index]) chunks[index] = array[i]
      else chunks[index] += stringSeparator + array[i]
      if (i % nb === nb - 1) index++
    }
  } else {
    // => push
    for (let i = 0; i < array.length; i++) {
      if (!chunks[index]) chunks[index] = []
      chunks[index].push(array[i])
      if (i % nb === nb - 1) index++
    }
  }
  return chunks
}

/**
 * A collection of regular expressions for common validation tasks.
 *
 * @property {RegExp} phoneRegExp - Validates various international and local phone number formats.
 *   Supports formats with country codes (+ or 00), spaces, dashes, dots, and parentheses.
 *
 * @property {RegExp} emailRegExp - Validates email addresses according to the local-part rules from
 *   https://en.wikipedia.org/wiki/Email_address#Local-part.
 *
 * @property {RegExp} urlRegExp - Validates HTTP and HTTPS URLs with domain names, optional ports,
 *   paths, query strings, and fragments.
 *
 * @example
 * validationRegExps.phoneRegExp.test("+33 1 23 45 67 89"); // true
 * validationRegExps.emailRegExp.test("example.user+test@example.com"); // true
 * validationRegExps.urlRegExp.test("https://www.example.com/path?query=123#anchor"); // true
 */
const validationRegExps = {
  phoneRegExp:
    /^(((\+|00)3\d[ _.-]?\d|(0[1-9]))((?:[ _.-]?(\d{2})){4}))$|^((\+|00)[12456789][\d _.-]{7,14})$|^(\+\(\d{1,3}\)[\d _.-]{7,9})$|^[\d _.-]{9,12}$/,
  emailRegExp:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+([a-zA-Z][-]{0,1}){2,}))$/,
  urlRegExp: /(http(s)?:\/\/)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/,
}

/**
 * Generates a regex that validates a local phone number by length.
 *
 * @param {number} length - Expected number of digits (nsnLength from ITU-T E.164).
 * @param {number} [tolerance=0] - Allow +/- digits (0 = exact match).
 * @returns {RegExp|null} A RegExp matching digits only, or null if length is invalid.
 *
 * @example
 * getPhoneLengthRegExp(9) // /^\d{9}$/ - matches exactly 9 digits
 * getPhoneLengthRegExp(10, 1) // /^\d{9,11}$/ - matches 9 to 11 digits
 * getPhoneLengthRegExp(0) // null
 */
function getPhoneLengthRegExp(length, tolerance = 0) {
  if (!length || length < 1) return null
  const min = Math.max(1, length - tolerance)
  const max = length + tolerance
  return new RegExp(`^\\d{${min},${max}}$`)
}

/**
 * Centralized NSN (National Significant Number) lengths by calling code.
 * Source: ITU-T E.164 standard.
 *
 * This map is the single source of truth for phone number validation lengths,
 * ensuring tenants don't lose validation when customizing country lists.
 *
 * Note: Some countries have variable-length numbers. Values here represent
 * the most common length (typically mobile). Use regExp override in list
 * items for exact validation when needed.
 */
const defaultNsnLengths = {
  "+93": 9, // Afghanistan
  "+355": 9, // Albania
  "+213": 9, // Algeria
  "+376": 6, // Andorra
  "+244": 9, // Angola
  "+54": 10, // Argentina
  "+374": 8, // Armenia
  "+297": 7, // Aruba
  "+61": 9, // Australia
  "+43": 10, // Austria
  "+994": 9, // Azerbaijan
  "+973": 8, // Bahrain
  "+880": 10, // Bangladesh
  "+375": 9, // Belarus
  "+32": 9, // Belgium
  "+501": 7, // Belize
  "+229": 8, // Benin
  "+975": 8, // Bhutan
  "+591": 8, // Bolivia
  "+387": 8, // Bosnia and Herzegovina
  "+267": 8, // Botswana
  "+55": 11, // Brazil
  "+673": 7, // Brunei
  "+359": 9, // Bulgaria
  "+226": 8, // Burkina Faso
  "+257": 8, // Burundi
  "+855": 9, // Cambodia
  "+237": 9, // Cameroon
  "+1": 10, // Canada / USA / NANP
  "+238": 7, // Cape Verde
  "+236": 8, // Central African Republic
  "+235": 8, // Chad
  "+56": 9, // Chile
  "+86": 11, // China
  "+57": 10, // Colombia
  "+269": 7, // Comoros
  "+682": 5, // Cook Islands
  "+506": 8, // Costa Rica
  "+385": 9, // Croatia
  "+53": 8, // Cuba
  "+599": 7, // Curaçao / Caribbean Netherlands
  "+357": 8, // Cyprus
  "+420": 9, // Czech Republic
  "+243": 9, // Democratic Republic of the Congo
  "+45": 8, // Denmark
  "+253": 8, // Djibouti
  "+670": 8, // East Timor
  "+593": 9, // Ecuador
  "+20": 10, // Egypt
  "+503": 8, // El Salvador
  "+240": 9, // Equatorial Guinea
  "+291": 7, // Eritrea
  "+372": 8, // Estonia
  "+251": 9, // Ethiopia
  "+500": 5, // Falkland Islands
  "+298": 6, // Faroe Islands
  "+679": 7, // Fiji
  "+358": 9, // Finland
  "+33": 9, // France
  "+689": 8, // French Polynesia
  "+241": 8, // Gabon
  "+220": 7, // Gambia
  "+995": 9, // Georgia
  "+49": 11, // Germany
  "+233": 9, // Ghana
  "+350": 8, // Gibraltar
  "+30": 10, // Greece
  "+299": 6, // Greenland
  "+502": 8, // Guatemala
  "+44": 10, // Guernsey / Isle of Man / Jersey / UK
  "+224": 9, // Guinea
  "+245": 7, // Guinea-Bissau
  "+592": 7, // Guyana
  "+509": 8, // Haiti
  "+504": 8, // Honduras
  "+852": 8, // Hong Kong
  "+36": 9, // Hungary
  "+354": 7, // Iceland
  "+91": 10, // India
  "+62": 10, // Indonesia
  "+98": 10, // Iran
  "+964": 10, // Iraq
  "+353": 9, // Ireland
  "+972": 9, // Israel
  "+39": 10, // Italy
  "+225": 10, // Ivory Coast
  "+81": 10, // Japan
  "+962": 9, // Jordan
  "+7": 10, // Kazakhstan / Russia
  "+254": 9, // Kenya
  "+686": 8, // Kiribati
  "+383": 8, // Kosovo
  "+965": 8, // Kuwait
  "+996": 9, // Kyrgyzstan
  "+856": 10, // Laos
  "+371": 8, // Latvia
  "+961": 8, // Lebanon
  "+266": 8, // Lesotho
  "+231": 9, // Liberia
  "+218": 9, // Libya
  "+423": 7, // Liechtenstein
  "+370": 8, // Lithuania
  "+352": 9, // Luxembourg
  "+853": 8, // Macau
  "+389": 8, // North Macedonia
  "+261": 9, // Madagascar
  "+265": 9, // Malawi
  "+60": 10, // Malaysia
  "+960": 7, // Maldives
  "+223": 8, // Mali
  "+356": 8, // Malta
  "+692": 7, // Marshall Islands
  "+222": 8, // Mauritania
  "+230": 8, // Mauritius
  "+262": 9, // Mayotte / Réunion
  "+52": 10, // Mexico
  "+691": 7, // Micronesia
  "+373": 8, // Moldova
  "+377": 8, // Monaco
  "+976": 8, // Mongolia
  "+382": 8, // Montenegro
  "+212": 9, // Morocco / Western Sahara
  "+258": 9, // Mozambique
  "+95": 10, // Myanmar
  "+264": 9, // Namibia
  "+674": 7, // Nauru
  "+977": 10, // Nepal
  "+31": 9, // Netherlands
  "+687": 6, // New Caledonia
  "+64": 9, // New Zealand / Pitcairn
  "+505": 8, // Nicaragua
  "+227": 8, // Niger
  "+234": 10, // Nigeria
  "+683": 4, // Niue
  "+850": 10, // North Korea
  "+47": 8, // Norway / Svalbard
  "+968": 8, // Oman
  "+92": 10, // Pakistan
  "+680": 7, // Palau
  "+970": 9, // Palestine
  "+507": 8, // Panama
  "+675": 8, // Papua New Guinea
  "+595": 9, // Paraguay
  "+51": 9, // Peru
  "+63": 10, // Philippines
  "+48": 9, // Poland
  "+351": 9, // Portugal
  "+974": 8, // Qatar
  "+242": 9, // Republic of the Congo
  "+40": 9, // Romania
  "+250": 9, // Rwanda
  "+590": 9, // Saint Barthélemy / Saint Martin
  "+290": 4, // Saint Helena
  "+508": 6, // Saint Pierre and Miquelon
  "+685": 7, // Samoa
  "+378": 10, // San Marino
  "+239": 7, // São Tomé and Príncipe
  "+966": 9, // Saudi Arabia
  "+221": 9, // Senegal
  "+381": 9, // Serbia
  "+248": 7, // Seychelles
  "+232": 8, // Sierra Leone
  "+65": 8, // Singapore
  "+421": 9, // Slovakia
  "+386": 8, // Slovenia
  "+677": 7, // Solomon Islands
  "+252": 8, // Somalia
  "+27": 9, // South Africa
  "+82": 10, // South Korea
  "+211": 9, // South Sudan
  "+34": 9, // Spain
  "+94": 9, // Sri Lanka
  "+249": 9, // Sudan
  "+597": 7, // Suriname
  "+268": 8, // Eswatini (Swaziland)
  "+46": 9, // Sweden
  "+41": 9, // Switzerland
  "+963": 9, // Syria
  "+886": 9, // Taiwan
  "+992": 9, // Tajikistan
  "+255": 9, // Tanzania
  "+66": 9, // Thailand
  "+228": 8, // Togo
  "+690": 4, // Tokelau
  "+676": 7, // Tonga
  "+216": 8, // Tunisia
  "+90": 10, // Turkey
  "+993": 8, // Turkmenistan
  "+688": 5, // Tuvalu
  "+256": 9, // Uganda
  "+380": 9, // Ukraine
  "+971": 9, // United Arab Emirates
  "+598": 9, // Uruguay
  "+998": 9, // Uzbekistan
  "+678": 7, // Vanuatu
  "+379": 10, // Vatican City
  "+58": 10, // Venezuela
  "+84": 10, // Vietnam
  "+681": 6, // Wallis and Futuna
  "+967": 9, // Yemen
  "+260": 9, // Zambia
  "+263": 9, // Zimbabwe
  // NANP territories (all use 10 digits like +1)
  "+1-684": 7, // American Samoa (local part only)
  "+1-264": 7, // Anguilla
  "+1-268": 7, // Antigua and Barbuda
  "+1-242": 7, // Bahamas
  "+1-246": 7, // Barbados
  "+1-441": 7, // Bermuda
  "+1-284": 7, // British Virgin Islands
  "+1-345": 7, // Cayman Islands
  "+1-767": 7, // Dominica
  "+1-809": 10, // Dominican Republic
  "+1-473": 7, // Grenada
  "+1-671": 7, // Guam
  "+1-876": 7, // Jamaica
  "+1-664": 7, // Montserrat
  "+1-670": 7, // Northern Mariana Islands
  "+1-787": 10, // Puerto Rico
  "+1-869": 7, // Saint Kitts and Nevis
  "+1-758": 7, // Saint Lucia
  "+1-784": 7, // Saint Vincent and the Grenadines
  "+1-721": 7, // Sint Maarten
  "+1-868": 7, // Trinidad and Tobago
  "+1-649": 7, // Turks and Caicos Islands
  "+1-340": 7, // U.S. Virgin Islands
  // Special territories
  "+672": 6, // Antarctica / Norfolk Island
  "+246": 7, // British Indian Ocean Territory
}

/**
 * Gets the NSN (National Significant Number) length for a calling code.
 *
 * This function provides centralized phone validation lookup, ensuring
 * tenants get proper validation even when customizing their country lists.
 *
 * Handles edge cases:
 * - Normalizes whitespace and trailing dashes
 * - NANP codes (+1-XXX, +1 XXX) return 10 for main number, lookup for local
 *
 * @param {string} callingCode - The calling code (e.g., "+33", "+1-684").
 * @returns {number|null} The NSN length, or null if not found.
 *
 * @example
 * getNsnLengthByCallingCode("+33")      // 9 (France)
 * getNsnLengthByCallingCode("+1")       // 10 (USA/Canada)
 * getNsnLengthByCallingCode("+1-684")   // 7 (American Samoa - local part)
 * getNsnLengthByCallingCode("+999")     // null (unknown)
 */
function getNsnLengthByCallingCode(callingCode) {
  if (!callingCode) return null

  // Normalize: trim, remove trailing dash/space
  const normalized = callingCode.trim().replace(/[-\s]+$/, "")

  // Check direct lookup first (handles +1-XXX NANP territories)
  if (defaultNsnLengths[normalized] !== undefined) {
    return defaultNsnLengths[normalized]
  }

  // NANP fallback: +1 alone or +1-XXX not in map defaults to 10
  if (normalized === "+1" || normalized.startsWith("+1-") || normalized.startsWith("+1 ")) {
    return 10
  }

  return null
}

/**
 * Generates a trigram (3-character string) from a given name according to specific rules.
 *
 * - Removes dots, commas, hyphens, digits, and trims whitespace before processing.
 * - Converts the name to uppercase.
 * - For 3 or more words: returns the first letter of the first three words.
 * - For 2 words with `firstTwoAndLastOne` true: returns first two letters plus last letter of the first word.
 * - For 2 words without `firstTwoAndLastOne`: returns first two letters of the first word plus first letter of the second word.
 * - For 1 word with `firstTwoAndLastOne` true: returns first two letters plus last letter.
 * - For 1 word without `firstTwoAndLastOne`: returns first three letters.
 *
 * @param {string} name - The input name string.
 * @param {boolean} [firstTwoAndLastOne=false] - Alters trigram generation for 1 or 2-word names.
 * @returns {string|undefined} The generated trigram, or undefined if input is falsy.
 *
 * @example
 * toTrigram("Docto Phone Paris");        // "DPP"
 * toTrigram("Docto Phone", true);        // "DOO"
 * toTrigram("Docto Phone");               // "DOE"
 * toTrigram("Doctolib", true);            // "DOB"
 * toTrigram("Doctolib");                  // "DOC"
 */
function toTrigram(name, firstTwoAndLastOne) {
  if (!name) return

  const _name = name.replaceAll(".", "").replaceAll(",", "").replaceAll("-", "").replace(/[0-9]/g, "").trim().toUpperCase()
  const words = _name.split(" ")

  // 3 words and more => Get first letter of the first 3 words (DOCTO PHONE PARIS => DPP)
  if (words.length > 2)
    return words
      .slice(0, 3)
      .map(word => word.substring(0, 1))
      .join("")

  // 2 words + firstTwoAndLastOne => 1st 2 letters + last letter of the 1st word (DOCTO PHONE => DOO)
  if (words.length === 2 && firstTwoAndLastOne) return words[0].substring(0, 2) + words[0].slice(-1)

  // 2 words => 1st 2 letters of the 1st word + last letter of the 2nd word (DOCTO PHONE => DOE)
  if (words.length === 2) return words[0].substring(0, 2) + words[1].substring(0, 1)

  // 1 word => 1st 2 letters + last letter (DOCTOLIB => DOB)
  if (firstTwoAndLastOne) return _name.substring(0, 2) + _name.slice(-1)

  // 1 word => 1st 3 letters (DOCTOLIB => DOC)
  return _name.substring(0, 3)
  //TODO : Should special characters be managed? ("GLINCHE SPORT & CLASSIC" => "GS&")
}

/**
 * Removes all diacritical marks (accents) from the given string using Unicode normalization.
 * It converts characters like "é" to "e", "ñ" to "n", etc.
 *
 * Note: This function does not currently handle certain ligatures or special characters like:
 * - "æ", "œ", "ø", "ð", "þ", "ß"
 * - Their uppercase forms: "Æ", "Œ", "Ø", "Ð", "Þ"
 * These cases are marked as TODOs in the implementation.
 *
 * @param {string} s - The input string from which accents should be removed.
 * @returns {string} - The normalized string without diacritical marks. If normalization is unsupported, returns the original string.
 *
 * @example
 * removeAllAccents("Crème brûlée"); // "Creme brulee"
 * removeAllAccents("façade naïve rôle"); // "facade naive role"
 */
function removeAllAccents(s) {
  if (!s?.normalize) return s
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
}

/**
 * Generates a filter function that tests whether a given value matches the provided filter string.
 * The filtering is case-insensitive and accent-insensitive, and supports AND/OR logic based on the filter format.
 *
 * Supported formats:
 * - Space-separated terms: implies AND logic.
 * - Comma-separated terms: implies OR logic.
 * - Quoted terms (`"term"`): implies AND logic with exact sequences.
 *
 * Handles various input types:
 * - Strings, numbers, booleans: converted to uppercase strings for comparison.
 * - Dates: formatted using the provided locale before comparison.
 * - Objects/arrays: stringified with `JSON.stringify()`, unless they are React components.
 *
 * @param {string} filter - The search string to build the filter function from.
 * @param {string} [locale] - Optional locale used for formatting Date objects.
 * @returns {(field: any) => boolean} - A predicate function that checks if a field matches the filter.
 *
 * @example
 * const filterFn = getFilterFunction("blue car");
 * filterFn("Blue sports car"); // true
 * filterFn("Blue truck"); // false
 *
 * @example
 * const filterFn = getFilterFunction("blue car");
 * filterFn("Fast blue car"); // true
 * filterFn("blue and red car"); // false
 *
 * @example
 * const filterFn = getFilterFunction("blue,red");
 * filterFn("red bike"); // true
 * filterFn("green scooter"); // false
 */
function getFilterFunction(filter, locale) {
  filter = removeAllAccents(filter).toUpperCase()

  function includes(field, parts, AND) {
    if (!field) return false

    if (field instanceof Date) field = formatDate(field, locale)
    // $$typeof ==> react component
    if ((typeof field === "object" || Array.isArray(field)) && !field.$$typeof) field = JSON.stringify(field) // obj/array => string

    const _field = removeAllAccents(field.toString().trim()).toUpperCase() // number, boolean => string
    if (AND) {
      for (const part of parts) if (!_field.includes(part)) return false
      return true
    } else {
      for (const part of parts) if (_field.includes(part)) return true
      return false
    }
  }

  let parts, AND
  // Quotes (")
  if (filter.includes(`"`)) {
    parts = filter
      .split(`"`)
      .map(part => part.trim())
      .filter(part => part)
    AND = true
  }
  // Comma (,)
  else if (filter.includes(",")) {
    parts = filter
      .split(",")
      .map(part => part.trim())
      .filter(part => part)
  }
  // No commas + No quotes
  else {
    parts = filter
      .split(" ")
      .map(part => part.trim())
      .filter(part => part)
    AND = true
  }
  return field => includes(field, parts, AND)
}

/**
 * Retrieves all "leaf" items from a nested structure, optionally filtering them using a condition function.
 *
 * A "leaf" item is considered an object that does not contain a nested `items` array.
 *
 * @param {Array} items - The array of items to traverse. Each item may optionally have a nested `items` array.
 * @param {Function} [conditionFunction] - Optional. A function to test each leaf item. Only items for which this function returns `true` are included in the result.
 * @param {Array} [result=[]] - Internal accumulator for recursive traversal. Do not provide this manually.
 * @returns {Array} An array of all leaf items that satisfy the condition (if provided).
 *
 * @example
 * // Basic usage with no filtering
 * getLeafItems([
 *   { name: "A", items: [{ name: "B" }, { name: "C" }] },
 *   { name: "D" }
 * ])
 * // Returns: [{ name: "B" }, { name: "C" }, { name: "D" }]
 *
 * @example
 * // Using a condition function
 * getLeafItems([
 *   { name: "A", items: [{ name: "B" }, { name: "C" }] },
 *   { name: "D" }
 * ], item => item.name === "D")
 * // Returns: [{ name: "D" }]
 */
function getLeafItems(items = [], conditionFunction, result = []) {
  for (const item of items) {
    if (Array.isArray(item.items)) result = getLeafItems(item.items, conditionFunction, result)
    else if ((conditionFunction && conditionFunction(item)) || !conditionFunction) {
      result.push(item)
    }
  }
  return result
}

/**
 * Converts a byte value into a human-readable string with appropriate units (KB, MB, or GB).
 *
 * The function defaults to displaying values in GB, but will automatically switch to MB or KB
 * if the value is small enough. It rounds the output to the nearest 0.005 unit using ceiling
 * for consistent formatting and returns a string with two decimal places and the appropriate unit.
 *
 * @param {number} bytes - The number of bytes to format.
 * @returns {string|number} A formatted string with the size in KB, MB, or GB. Returns the input if not a number.
 *
 * @example
 * formatBytes(1073741824) // "1.00 GB"
 * formatBytes(1048576) // "1.00 MB"
 * formatBytes(1024) // "1.00 KB"
 * formatBytes("abc") // "abc"
 */
function formatBytes(bytes) {
  if (isNaN(bytes)) return bytes

  let unit = "GB"
  let computedBytes = bytes / 1024 / 1024 / 1024
  if (computedBytes < 0.001) {
    unit = "KB"
    computedBytes *= 1024 * 1024
  } else if (computedBytes < 1) {
    unit = "MB"
    computedBytes *= 1024
  }

  return `${(Math.ceil(computedBytes * 200) / 200).toFixed(2)} ${unit}`
}

/**
 * Formats a SIREN number by grouping its digits into blocks of three separated by spaces.
 *
 * @param {string} siren - The SIREN number as a continuous string of digits.
 * @returns {string|undefined} The formatted SIREN with groups of three digits separated by spaces,
 *                            or undefined if the input is falsy.
 *
 * @example
 * formatSiren("123456789") // "123 456 789"
 * formatSiren("") // undefined
 */
function formatSiren(siren) {
  if (!siren) return
  return siren.match(/.{1,3}/g).join(" ")
}

/**
 * Formats a VAT number by inserting spaces at specific positions for readability.
 *
 * Spaces are added after the 2nd, 4th, 7th, and 10th characters.
 * Any existing spaces in the input are removed before formatting.
 *
 * @param {string} vat - The VAT number as a continuous string (may include spaces).
 * @returns {string|undefined} The formatted VAT number with spaces inserted, or undefined if input is falsy.
 *
 * @example
 * formatVAT("FR12345678901") // "FR 12 345 678 901"
 * formatVAT("FR 12 345678901") // "FR 12 345 678 901"
 * formatVAT("") // undefined
 */
function formatVAT(vat) {
  if (!vat) return
  vat = vat.replace(/ /g, "") // Remove " "

  let formattedVat = ""
  for (let i = 0; i < vat.length; i++) {
    if ([2, 4, 7, 10].includes(i)) formattedVat += " "
    formattedVat += vat[i]
  }
  return formattedVat
}

/**
 * Formats a phone number by removing the country code "+33" and adding spaces every two digits.
 *
 * If the number does not start with "0" after removing "+33", it prepends a "0".
 * Spaces are inserted after every two digits for readability.
 *
 * @param {string} phone - The phone number string, possibly including "+33" and spaces.
 * @returns {string|undefined} The formatted phone number or undefined if input is falsy.
 *
 * @example
 * formatPhone("+33123456789") // "01 23 45 67 89"
 * formatPhone("0123456789") // "01 23 45 67 89"
 * formatPhone("") // undefined
 */
function formatPhone(phone) {
  if (!phone) return
  phone = phone.replace(/\+33/g, "").replace(/ /g, "") //TODO : Only manages French phone numbers

  if (!phone.startsWith("0")) phone = `0${phone}` //TODO : Don't know how to manage special numbers

  let formattedPhone = ""
  for (let i = 0; i < phone.length; i++) {
    if (i % 2 === 0 && i !== 0) formattedPhone += " "
    formattedPhone += phone[i]
  }
  return formattedPhone
}

/**
 * Converts Lambert-93 coordinates (Lambert Easting and Northing) to WGS84 GPS coordinates (longitude and latitude).
 * https://gist.github.com/blemoine/e6045ed93b3d90a52891?permalink_comment_id=3015607
 *
 * Uses the Lambert-93 projection constants and iterative calculations to approximate latitude and longitude
 * in the WGS84 system.
 *
 * @param {number} lambertE - The Lambert-93 easting coordinate.
 * @param {number} lambertN - The Lambert-93 northing coordinate.
 * @returns {{longitude: number, latitude: number}} The corresponding GPS coordinates in decimal degrees.
 *
 * @example
 * lambert93toWGPS(700000, 6600000)
 * // { longitude: 3.0, latitude: 47.0 }
 *
 * @example
 * lambert93toWGPS(1029702.40, 2727231.49)
 * // { longitude: 1.44367, latitude: 43.60450 }
 */
function lambert93toWGPS(lambertE, lambertN) {
  const constantes = {
    GRS80E: 0.081819191042816,
    LONG_0: 3,
    XS: 700000,
    YS: 12655612.0499,
    n: 0.725607765053267,
    C: 11754255.4261,
  }

  const delX = lambertE - constantes.XS
  const delY = lambertN - constantes.YS
  const gamma = Math.atan(-delX / delY)
  const R = Math.sqrt(delX * delX + delY * delY)
  const latiso = Math.log(constantes.C / R) / constantes.n
  const sinPhiit0 = Math.tanh(latiso + constantes.GRS80E * Math.atanh(constantes.GRS80E * Math.sin(1)))
  const sinPhiit1 = Math.tanh(latiso + constantes.GRS80E * Math.atanh(constantes.GRS80E * sinPhiit0))
  const sinPhiit2 = Math.tanh(latiso + constantes.GRS80E * Math.atanh(constantes.GRS80E * sinPhiit1))
  const sinPhiit3 = Math.tanh(latiso + constantes.GRS80E * Math.atanh(constantes.GRS80E * sinPhiit2))
  const sinPhiit4 = Math.tanh(latiso + constantes.GRS80E * Math.atanh(constantes.GRS80E * sinPhiit3))
  const sinPhiit5 = Math.tanh(latiso + constantes.GRS80E * Math.atanh(constantes.GRS80E * sinPhiit4))
  const sinPhiit6 = Math.tanh(latiso + constantes.GRS80E * Math.atanh(constantes.GRS80E * sinPhiit5))

  const longRad = Math.asin(sinPhiit6)
  const latRad = gamma / constantes.n + (constantes.LONG_0 / 180) * Math.PI

  const longitude = (latRad / Math.PI) * 180
  const latitude = (longRad / Math.PI) * 180

  return { longitude, latitude }
}

/**
 * Formats an address object into a single string by concatenating available address parts,
 * or falls back to coordinates or a full address line if needed.
 *
 * The function tries to combine `address1`, `address2`, `zipcode`, and `city` separated by spaces.
 * If none of these are available but `location.coordinates` exist, it returns the coordinates
 * rounded to 5 decimal places as "longitude,latitude".
 * If neither is available, it returns the `addressLine` property.
 *
 * @param {Object} address - The address object to format.
 * @param {string} [address.address1] - First line of the address.
 * @param {string} [address.address2] - Second line of the address.
 * @param {string} [address.zipcode] - Postal code.
 * @param {string} [address.city] - City name.
 * @param {Object} [address.location] - Location object containing coordinates.
 * @param {number[]} [address.location.coordinates] - Array with longitude and latitude.
 * @param {string} [address.addressLine] - Full address line fallback.
 * @returns {string|undefined} The formatted address string or undefined if input is falsy.
 *
 * @example
 * formatAddress({
 *   address1: "10 Downing St",
 *   address2: "Westminster",
 *   zipcode: "SW1A 2AA",
 *   city: "London"
 * }) // "10 Downing St Westminster SW1A 2AA London"
 *
 * @example
 * formatAddress({
 *   location: { coordinates: [2.2945, 48.8584] }
 * }) // "2.2945,48.8584"
 *
 * @example
 * formatAddress({
 *   addressLine: "Some full address line"
 * }) // "Some full address line"
 *
 * @example
 * formatAddress(null) // undefined
 */
function formatAddress(address) {
  if (!address) return address
  let ret = [address.address1, address.address2, address.zipcode, address.city].filter(it => it).join(" ")
  if (!ret && address.location?.coordinates) {
    // precision 1 meter
    const x = Math.round(address.location.coordinates[0] * 100000) / 100000
    const y = Math.round(address.location.coordinates[1] * 100000) / 100000
    if (x && y) ret = x.toString() + "," + y.toString()
  }
  return ret || address.addressLine
}

/**
 * Checks if two inputs intersect in terms of shared elements.
 *
 * If both inputs are arrays, returns true if they share at least one common element.
 * If one input is an array and the other is a single value, returns true if the value exists in the array.
 * If both inputs are single values, returns true if they are strictly equal.
 *
 * @param {any|any[]} obj1 - The first value or array to check.
 * @param {any|any[]} obj2 - The second value or array to check.
 * @returns {boolean} True if there is any intersection or equality, false otherwise.
 *
 * @example
 * arrayIntersects([1, 2, 3], [3, 4, 5]) // true
 * arrayIntersects([1, 2, 3], 2) // true
 * arrayIntersects(5, [1, 2, 3]) // false
 * arrayIntersects(5, 5) // true
 * arrayIntersects(null, [1, 2]) // false
 */
function arrayIntersects(obj1, obj2) {
  if (Array.isArray(obj1)) {
    if (Array.isArray(obj2)) {
      for (const it1 of obj1) {
        for (const it2 of obj2) {
          if (it1 === it2) return true
        }
      }
    } else if (obj2) {
      for (const it1 of obj1) {
        if (it1 === obj2) return true
      }
    }
  } else if (obj1) {
    if (Array.isArray(obj2)) {
      for (const it2 of obj2) {
        if (it2 === obj1) return true
      }
    } else if (obj2) {
      if (obj1 === obj2) return true
    }
  }
  return false
}

/**
 * Credits to https://github.com/epoberezkin/fast-deep-equal v3.1.3
 * On npm this package is much more downloaded per week than lodash isEqual (43M vs 10M).
 * This site https://www.measurethat.net/Benchmarks/Show/11503/1/lodashisequal-vs-fast-deep-equal#latest_results_block
 * shows on Chromium that lodash isEqual is a bit faster than the other package
 * but on Firefox lodash isEqual is much slower by several orders of magnitude.
 *
 * Deeply compares two values for equality.
 *
 * Supports primitive types, arrays, objects, Maps, Sets, typed arrays,
 * regular expressions, and objects with custom `valueOf` or `toString` methods.
 * Handles `NaN` as equal to `NaN`.
 *
 * Note: Property order in objects is not considered for inequality (objects with same keys and values
 * but different key order are considered equal).
 *
 * @param {*} a - The first value to compare.
 * @param {*} b - The second value to compare.
 * @returns {boolean} True if the two values are deeply equal, false otherwise.
 *
 * @example
 * isEqual(1, 1) // true
 * isEqual([1, 2], [1, 2]) // true
 * isEqual({ a: 1, b: 2 }, { b: 2, a: 1 }) // true
 * isEqual(new Set([1, 2]), new Set([2, 1])) // true
 * isEqual(NaN, NaN) // true
 * isEqual(/abc/i, /abc/i) // true
 */
function isEqual(a, b) {
  if (a === b) return true

  if (a && b && typeof a == "object" && typeof b == "object") {
    if (a.constructor !== b.constructor) return false

    let length
    let i
    let keys
    if (Array.isArray(a)) {
      length = a.length
      if (length != b.length) return false
      for (i = length; i-- !== 0; ) if (!isEqual(a[i], b[i])) return false
      return true
    }

    if (a instanceof Map && b instanceof Map) {
      if (a.size !== b.size) return false
      for (i of a.entries()) if (!b.has(i[0])) return false
      for (i of a.entries()) if (!isEqual(i[1], b.get(i[0]))) return false
      return true
    }

    if (a instanceof Set && b instanceof Set) {
      if (a.size !== b.size) return false
      for (i of a.entries()) if (!b.has(i[0])) return false
      return true
    }

    if (ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
      length = a.length
      if (length != b.length) return false
      for (i = length; i-- !== 0; ) if (a[i] !== b[i]) return false
      return true
    }

    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags
    const aPrimitiveInfo = toComparablePrimitiveInfo(a)
    const bPrimitiveInfo = toComparablePrimitiveInfo(b)
    if (aPrimitiveInfo || bPrimitiveInfo) {
      if (!aPrimitiveInfo || !bPrimitiveInfo) return false

      if (aPrimitiveInfo._valueOf !== undefined || bPrimitiveInfo._valueOf !== undefined) {
        if (aPrimitiveInfo._valueOf === undefined || bPrimitiveInfo._valueOf === undefined) return false
        return Object.is(aPrimitiveInfo._valueOf, bPrimitiveInfo._valueOf)
      }

      if (aPrimitiveInfo._toString !== undefined || bPrimitiveInfo._toString !== undefined) {
        if (aPrimitiveInfo.toString === undefined || bPrimitiveInfo.toString === undefined) return false
        return Object.is(aPrimitiveInfo._toString, bPrimitiveInfo._toString)
      }
    }

    keys = Object.keys(a)
    length = keys.length
    if (length !== Object.keys(b).length) return false

    for (i = length; i-- !== 0; ) if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false

    for (i = length; i-- !== 0; ) {
      const key = keys[i]
      if (!isEqual(a[key], b[key])) return false
    }

    //TODO : In javascript, the order of properties is truly stored, so it should be false :
    //a = { x : 45, y: "toto" }, b = { y: "toto", x : 45 } => Return true instead of false!
    return true
  }

  // true if both NaN, false otherwise
  return a !== a && b !== b
}

/**
 * Helper for {@link isEqual}: extracts `valueOf` / `toString` primitives from boxed or exotic objects.
 *
 * @param {*} value - Any value.
 * @returns {{ _valueOf?: *, _toString?: string }|null} Descriptor for comparison, or `null` if not applicable.
 * @internal
 */
function toComparablePrimitiveInfo(value) {
  if (value == null || (typeof value !== "object" && typeof value !== "function")) return null
  const primitiveInfo = {}

  try {
    const primitiveValue = value.valueOf()
    if (primitiveValue !== value && (typeof primitiveValue !== "object" || primitiveValue === null)) primitiveInfo._valueOf = primitiveValue
  } catch {}

  try {
    const stringValue = value.toString()
    if (typeof stringValue === "string" && stringValue !== "[object Object]") primitiveInfo._toString = stringValue
  } catch {}

  return Object.keys(primitiveInfo).length ? primitiveInfo : null
}

const coreCatalogNames = {
  FINANCING: "financing",
  BANKING: "banking",
}

/**
 * Randomly shuffles the elements of an array in place using the Fisher-Yates algorithm.
 *
 * @param {any[]} array - The array to shuffle.
 * @returns {any[]} The same array instance with its elements shuffled.
 *
 * @example
 * shuffleArray([1, 2, 3, 4])
 * // Possible output: [3, 1, 4, 2]
 */
function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = array[i]
    array[i] = array[j]
    array[j] = temp
  }
  return array
}

/**
 * Checks if a string is a valid UUID (version agnostic).
 *
 * A valid UUID must follow the pattern: 8-4-4-4-12 hexadecimal characters, separated by hyphens.
 *
 * @param {string} uuid - The string to validate as a UUID.
 * @returns {boolean} True if the string is a valid UUID, false otherwise.
 *
 * @example
 * isValidUuid("123e4567-e89b-12d3-a456-426614174000") // true
 * isValidUuid("invalid-uuid-string") // false
 */
function isValidUuid(uuid) {
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
  return uuidRegex.test(uuid)
}

/**
 * Validates a username based on several criteria, including optional tenant domain.
 * Warning: check order matters here.
 *
 * The username is valid if:
 * - It is a non-empty string.
 * - It matches an email regex pattern.
 * - It is a numeric string.
 * - If a tenant is provided, the username must end with `@tenant`.
 * - It contains an "@" not at the end.
 * - It does not contain forbidden characters (only letters, digits, `.`, `-`, `_`, `@`, and accented letters allowed).
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.username - The username to validate.
 * @param {string} [params.tenant] - Optional tenant domain to validate against.
 * @returns {boolean|undefined} True if valid, otherwise undefined.
 *
 * @example
 * isValidUsername({ username: "user@example.com" }) // true
 * isValidUsername({ username: "12345" }) // true
 * isValidUsername({ username: "user@tenant.com", tenant: "tenant.com" }) // true
 * isValidUsername({ username: "user@other.com", tenant: "tenant.com" }) // undefined
 * isValidUsername({ username: "invalid$user" }) // undefined
 */
function isValidUsername({ username, tenant }) {
  if (!username || typeof username !== "string") return

  if (validationRegExps.emailRegExp.test(username)) return true
  if (!isNaN(username)) return true

  if (tenant && !username.endsWith(`@${tenant}`)) return
  // must contain an @ and it must not be at the end
  if (username.indexOf("@") === -1 || username.endsWith("@")) return
  // must not contain forbidden characters
  if (username.match(/[^a-zA-Z0-9.\-_@À-ÖØ-öø-ÿ]+/)) return
  return true
}

/**
 * Evaluates whether a document matches a simplified MongoDB-like query object.
 *
 * Very basic reproduction of the MongoDB query engine used to evaluate queries against documents without having to insert them in the database.
 * Warning: this function must absolutely be a subset of what is capable the real MongoDB query engine,
 * because it is used to validate queries before executing them against MongoDB.
 *
 * Supports:
 * - Exact matches on document fields.
 * - `$in` operator for arrays of allowed values.
 * - Nested sub-document queries with dot notation (e.g., "persons.personRegistration").
 * - `$or` queries with arrays of sub-queries.
 *
 * Note: Only supports two-level nesting in dot notation and limited operators as described.
 *
 * @param {Object} document - The document to test against the query.
 * @param {Object} query - The query object with conditions to match.
 * @returns {boolean|undefined} True if the document matches the query, false if not, or undefined if inputs are invalid.
 *
 * @example
 * const doc = {
 *   orgaRegistration: "O1",
 *   persons: [
 *     { personRegistration: "P1" },
 *     { personRegistration: "P2" }
 *   ]
 * };
 *
 * const query1 = { orgaRegistration: { $in: ["O1", "O2"] } };
 * evalDbQueryMatch(doc, query1) // true
 *
 * @example
 * const query2 = {
 *   $or: [
 *     { "persons.personRegistration": "P3" },
 *     { orgaRegistration: "O1" }
 *   ]
 * };
 * evalDbQueryMatch(doc, query2) // true
 *
 * @example
 * const query3 = { "persons.personRegistration": { $in: ["P3", "P4"] } };
 * evalDbQueryMatch(doc, query3) // false
 */
function evalDbQueryMatch(document, query) {
  if (!document || typeof document !== "object" || !query || typeof query !== "object") return

  return Object.keys(query).every(key => {
    const queryValue = query[key]

    if (key === "$or") {
      return queryValue.some($or => {
        if (typeof $or === "object") {
          return Object.keys($or).every(key => {
            const _queryValue = $or[key]
            // Ex: "persons.personRegistration"
            if (key.includes(".") && key.split(".").length === 2) {
              const [subDocumentsKey, subDocumentKey] = key.split(".")
              const subDocuments = document[subDocumentsKey] // persons array

              if (Array.isArray(subDocuments)) {
                if (typeof _queryValue === "object" && Object.keys(_queryValue).length === 1 && Array.isArray(_queryValue.$in))
                  return subDocuments.find(subDocument => _queryValue.$in.includes(subDocument[subDocumentKey]))

                return subDocuments.find(subDocument => subDocument[subDocumentKey] === _queryValue)
              }
            } else {
              const documentValue = document[key]
              // Ex: { orgaRegistration: { $in: ["O1"] } }
              if (typeof _queryValue === "object" && Object.keys(_queryValue).length === 1 && Array.isArray(_queryValue.$in))
                return _queryValue.$in.includes(documentValue)
            }
          })
        }
        return false
      })
    } else {
      if (key.includes(".")) {
        const [subDocumentsKey, subDocumentKey] = key.split(".")
        const subDocuments = document[subDocumentsKey] // persons array

        if (Array.isArray(subDocuments)) {
          let queryValues
          if (Array.isArray(queryValue)) queryValues = queryValue
          else if (typeof queryValue === "object" && Object.keys(queryValue).length === 1 && Array.isArray(queryValue.$in))
            queryValues = queryValue.$in

          if (queryValues) return subDocuments.find(subDocument => queryValues.includes(subDocument[subDocumentKey]))
          return subDocuments.find(subDocument => subDocument[subDocumentKey] === queryValue)
        }
      } else {
        const documentValue = document[key]

        if (Array.isArray(queryValue)) return queryValue.includes(documentValue)
        if (
          typeof queryValue === "object" &&
          Object.keys(queryValue).length === 1 &&
          (Array.isArray(queryValue.$in) || Array.isArray(queryValue.$nin))
        )
          return queryValue.$nin ? !queryValue.$nin.includes(documentValue) : queryValue.$in.includes(documentValue)

        return queryValue === documentValue
      }
    }
  })
}

/**
 * Parses a value into a boolean.
 *
 * Returns `true` if the input is:
 * - The boolean `true`
 * - The string `"true"`
 * - A value that parses to the integer `1`
 * Otherwise, returns `false`.
 *
 * @param {any} bool - The value to parse.
 * @returns {boolean} The parsed boolean.
 *
 * @example
 * parseBoolean(true) // true
 * parseBoolean("true") // true
 * parseBoolean("1") // true
 * parseBoolean(1) // true
 * parseBoolean(false) // false
 * parseBoolean("0") // false
 */
function parseBoolean(bool) {
  if (bool === true || bool === "true") return true
  if (parseInt(bool) === 1) return true
  return false
}

/**
 * Parses a frequency string and returns an object describing the frequency type,
 * along with a label optionally using plural form.
 *
 * @param {string} frequency - The frequency value as a string. Expected values:
 *   - "1" for daily
 *   - "7" for weekly
 *   - "30" for monthly
 *   - "90" for quarterly
 *   - "180" for semester
 *   - "365" or "360" for annual
 * @param {Object} [options] - Optional settings.
 * @param {boolean} [options.usePlural=false] - Whether to use the plural form for the label.
 * @returns {Object} An object containing:
 *   - {boolean} isDaily - True if frequency is daily.
 *   - {boolean} isWeekly - True if frequency is weekly.
 *   - {boolean} isMonthly - True if frequency is monthly.
 *   - {boolean} isQuarter - True if frequency is quarterly.
 *   - {boolean} isSemester - True if frequency is semester.
 *   - {boolean} isAnnual - True if frequency is annual.
 *   - {string} label - The frequency label, pluralized if requested.
 *
 * @example
 * // Single day, singular label
 * parseFrequencyValue("1")
 * // Returns:
 * // {
 * //   isDaily: true,
 * //   isWeekly: false,
 * //   isMonthly: false,
 * //   isQuarter: false,
 * //   isSemester: false,
 * //   isAnnual: false,
 * //   label: "day"
 * // }
 *
 * @example
 * // Weekly frequency with plural label
 * parseFrequencyValue("7", { usePlural: true })
 * // Returns:
 * // {
 * //   isDaily: false,
 * //   isWeekly: true,
 * //   isMonthly: false,
 * //   isQuarter: false,
 * //   isSemester: false,
 * //   isAnnual: false,
 * //   label: "weeks"
 * // }
 *
 * @example
 * // Annual frequency with plural label
 * parseFrequencyValue("365", { usePlural: true })
 * // Returns:
 * // {
 * //   isDaily: false,
 * //   isWeekly: false,
 * //   isMonthly: false,
 * //   isQuarter: false,
 * //   isSemester: false,
 * //   isAnnual: true,
 * //   label: "years"
 * // }
 */
function parseFrequencyValue(frequency, { usePlural } = {}) {
  const isDaily = frequency === "1"
  const isWeekly = frequency === "7"
  const isMonthly = frequency === "30"
  const isQuarter = frequency === "90"
  const isSemester = frequency === "180"
  const isAnnual = ["365", "360"].includes(frequency)
  return {
    isDaily,
    isWeekly,
    isMonthly,
    isQuarter,
    isSemester,
    isAnnual,
    label: isDaily
      ? usePlural
        ? "days"
        : "day"
      : isWeekly
        ? usePlural
          ? "weeks"
          : "week"
        : isMonthly
          ? usePlural
            ? "months"
            : "month"
          : isQuarter
            ? usePlural
              ? "quarters"
              : "quarter"
            : isSemester
              ? usePlural
                ? "semesters"
                : "semester"
              : isAnnual
                ? usePlural
                  ? "years"
                  : "year"
                : "",
  }
}

/**
 * Escapes special characters in a string so it can be safely used inside a
 * regular expression as a literal.
 *
 * Special regex characters like `. * + ? ^ $ { } ( ) | [ ] \` are prefixed
 * with a backslash, ensuring the input string is matched literally.
 *
 * @param {string} str - The input string to escape.
 * @returns {string} A new string with regex metacharacters escaped.
 *
 * @example
 * // Escapes "." and "?"
 * const safe = escapeRegex("hello.world?");
 * const regex = new RegExp("^" + safe);
 * console.log(regex.test("hello.world?")); // true
 * console.log(regex.test("helloXworld?")); // false
 */
function escapeRegExp(string) {
  return string.replace(/[.*+?${}()|&[\]\\]/g, "\\$&")
}

/**
 * These are scripts that don't support users' version.
 * In other words, when users change them online, they must publish them to activate their changes.
 */
const globalScripts = {
  "tenant-config": true,
  profilesPermissions: true,
  scriptAliases: true,
  "collection-extension": true,
  "string-overrides": true,
  "dyno-descriptors": true,
  "cron-list": true,
}

const entityPresenceHeartbeatMs = 15 * 1000
const entityPresenceActions = {
  view: "view",
  edit: "edit",
  lock: "lock",
  unlock: "unlock",
  quit: "quit",
  peek: "peek",
}

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/Server-sent_events/Using_server-sent_events#event_stream_format
 */
function parseSseString(eventString) {
  const sseObject = {}
  const lines = eventString.split("\n")

  for (const line of lines) {
    if (line.startsWith("id:")) {
      sseObject.id = line.slice(3).trim()
    } else if (line.startsWith("retry:")) {
      sseObject.retry = line.slice(6).trim()
    } else if (line.startsWith("event:")) {
      sseObject.event = line.slice(6).trim()
    } else if (line.startsWith("data:")) {
      sseObject.data = line.slice(5).trim()
    }
  }

  return sseObject
}

// Full-stack scripts are called by basikon-client directly, that is, without depth.
// They are also executed server side.
const fullStackScripts = {
  addressToString: "address-to-string",
  aiCatalog: "aiCatalog",
  amendmentComputation: "amendment-computation",
  amendmentsCatalog: "amendments-catalog",
  assetComputation: "asset-computation",
  bankOperationsCatalog: "bank-operations-catalog",
  bankProductsCatalog: "bank-products-catalog",
  calendars: "calendars",
  caseComputation: "case-computation",
  cashflowComputation: "cashflow-computation",
  contractComputation: "contract-computation",
  contractLotComputation: "contractLot-computation",
  covenantComputation: "covenant-computation",
  creditLineComputation: "creditLine-computation",
  depreciationScheduleComputation: "depreciation-schedule-computation",
  dynoDescriptors: "dyno-descriptors", // will be removed one day
  eligibilityKpis: "eligibility-kpis",
  eligibilityRules: "eligibility-rules",
  invoiceComputation: "invoice-computation",
  maintenanceComputation: "maintenance-computation",
  personRegistrationFormatCheck: "personRegistration-format-check",
  projectComputation: "project-computation",
  projectEstimateComputation: "projectEstimate-computation",
  scoreComputation: "score-computation",
  resourcesCostsCatalog: "resources-costs-catalog",
  stringOverrides: "string-overrides",
  forbiddenWords: "forbidden-words",
  /**
   * The user configuration script is an exception.
   * It is executed server side only, but it contains options exclusively intended for front-ends,
   * and should remain so, don't add backend options in it, they should be put in the tenant config instead.
   */
  userConfiguration: "user-configuration",
}

/**
 * Scripts that are executed only in the front-end.
 */
const frontScripts = {
  acbToAsset: "acb-to-asset",
  amendmentReasons: "amendment-reasons",
  assetCatalogItemsSearch: "assetCatalog/items-search",
  assetCatalogItemToAsset: "assetCatalogItem-to-asset",
  autobizToAsset: "autobiz-to-asset",
  autovistaToAsset: "autovista-to-asset",
  calendarTasks: "calendarTasks",
  camtExecute: "camt-execute",
  cartellToAsset: "cartell-to-asset",
  contractLotContracts: "contractLot-contracts",
  // This one is pure demo and must be move out.
  dashboardChartistBankAccount: "dashboard-chartist-bank-account",
  deviceValues: "devices-values",
  documentTypesWithWorkflow: "document-types-with-workflow",
  documentEntitiesWithWorkflow: "document-entities-with-workflow",
  elementsCatalog: "elements-catalog",
  emprekisToAsset: "emprekis-to-asset",
  exportsCatalog: "exports-catalog",
  fundingPayments: "funding-payments",
  getAgencies: "get-agencies",
  helloWorld: "hello-world",
  icareAssetQuotations: "icare-asset-quotations",
  importCreditLineDraw: "import-credit-line-draw",
  importPageTemplateGroups: "imports-page-template-groups",
  initTimeEntries: "init-time-entries",
  lecturaToAsset: "lectura-to-asset",
  productElementCatalogItemToInvoiceLine: "productElementCatalogItem-to-invoiceLine",
  quickAddRelations: "quick-add-relations",
  runBilling: "run-billing",
  runInventories: "run-inventories",
  stringToAddress: "string-to-address",
  userContentConfig: "user-content-config",
  virtualKeyboards: "virtual-keyboards",
}

/**
 * Scripts that are not direct are called with depth, so this information can be used inside them to restrict their execution.
 */
const serverScripts = {
  ...fullStackScripts,
  agreementPersonsCheck: "agreementPersons-check",
  aiCompletion: "aiCompletion",
  alertOnUserImpersonation: "alert-on-user-impersonation",
  apiOverridePerson: "apiOverride-person",
  // This a demo script
  bankRates: "bank-rates",
  bannedPasswords: "bannedPasswords",
  bskWalletConfig: "bskWallet/config",
  bskWalletGetDataForNewUser: "bskWallet/getDataForNewUser",
  bskWalletNewUserSuccess: "bskWallet/newUserSuccess",
  camtParse: "camt-parse",
  camtEntries: "camt-entries",
  camtMatch: "camt-match",
  checkTimeEntries: "check-time-entries",
  collectionExtension: "collection-extension",
  cronList: "cron-list",
  databaseIndexes: "database-indexes",
  dynoDescriptors: "dyno-descriptors",
  exportDocumentsZipResult: "export-documents-zip-result",
  generateSepaDocument: "generate-sepa-document",
  gettingStartedToken: "gs-check-token",
  oidcLogin: "oidc-login",
  otpSendCode: "otp-send-code",
  postRunSepa: "post-run-sepa",
  productsCatalog: "products-catalog",
  profilesPermissions: "profilesPermissions",
  scriptAliases: "scriptAliases",
  resetPassword: "reset-password",
  resetPasswordSuccess: "reset-password-success",
  samlLogin: "saml-login",
  scimMapping: "scim-mapping",
  ssoPartnerLogin: "sso-partner-login",
  syncUserPersons: "sync-user-persons",
  tenantConfig: "tenant-config",
  termsOfService: "terms-of-service",
  recoverIdentifier: "recover-identifier",
  recoverIdentifierSuccess: "recover-identifier-success",
}

const serverScriptsExternal = {
  addressExtraInfo: "address-extra-info",
  ariadnextConnect: "ariadnext-connect",
  authorizedSiren: "authorizedSiren",
  contoConnect: "conto-connect",
  docusignConnect: "docusign-connect",
  dokobitConnect: "dokobit-connect",
  leasecom: "leasecom",
  steloDocumentsDatas: "stelo-documents-datas",
  tagpayConnect: "tagpay-connect",
  universignConnect: "universign-connect",
  webhookAmlFactory: "webhook-aml-factory",
  webhookApscan: "webhook-apscan",
  webhookFintegry: "webhook-fintegry",
  webhookPaybox: "webhook-paybox",
  webhookPayzen: "webhook-payzen",
  webhookPitneyBowes: "webhook-pitney-bowes",
  webhookFranfinance: "webhook-franfinance",
  webhookGgvie: "webhook-ggvie",
  yousignConnect: "yousign-connect",
  yousignv3Connect: "yousignv3-connect",
}

export {
  arrayIntersects,
  chunk,
  computePersonName,
  coreCatalogNames,
  defaultNsnLengths,
  entityPresenceActions,
  entityPresenceHeartbeatMs,
  escapeRegExp,
  evalDbQueryMatch,
  formatAddress,
  formatBytes,
  formatPhone,
  formatSiren,
  formatVAT,
  frontScripts,
  fullStackScripts,
  getFilterFunction,
  getLeafItems,
  getNsnLengthByCallingCode,
  getPhoneLengthRegExp,
  globalScripts,
  imageAllowedExtensions,
  isEqual,
  isValidUsername,
  isValidUuid,
  lambert93toWGPS,
  parseBoolean,
  parseFrequencyValue,
  parseSseString,
  removeAllAccents,
  serverScripts,
  serverScriptsExternal,
  shuffleArray,
  sleep,
  SUPPORTED_IMAGE_FORMATS_FOR_PDF_CONVERSION,
  SUPPORTED_SOURCE_FORMATS_FOR_PDF_CONVERSION,
  toDashed,
  toPlural,
  toSingular,
  toTrigram,
  validationRegExps,
}
